import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import type { VerifiedSkills_CareerBand as VerifiedSkillsCareerBand } from '__generated__/graphql-types';

import GetOccupationGroupAndCareerBandQuery from 'bundles/skills-common/shared/verified-skills/data/skill-pathway/GetOccupationGroupAndCareerBand.graphql';
import type {
  OccupationGroupFragment,
  GetOccupationGroupAndCareerBandQuery as QueryResult,
  GetOccupationGroupAndCareerBandQueryVariables as QueryVariables,
} from 'bundles/skills-common/shared/verified-skills/data/skill-pathway/__generated__/GetOccupationGroupAndCareerBand';

type Props = {
  courseId?: QueryVariables['courseId'];
  skip?: boolean;
};

const useGetOccupationGroupAndCareerBand = ({
  courseId,
  skip,
}: Props): {
  loading: boolean;
  occupationGroup?: OccupationGroupFragment | null;
  careerBand?: VerifiedSkillsCareerBand;
} => {
  const { data, loading, error } = useQuery<QueryResult, QueryVariables>(GetOccupationGroupAndCareerBandQuery, {
    variables: {
      courseId: courseId || '',
    },
    skip: skip || !courseId,
    fetchPolicy: 'cache-and-network',
    context: {
      clientName: 'gatewayGql',
    },
    notifyOnNetworkStatusChange: true,
  });

  if (error) {
    Sentry.captureException(error);

    return {
      occupationGroup: null,
      careerBand: undefined,
      loading: false,
    };
  }

  // Every verified skills course has a single occupation group and career band
  const occupationGroupAndCareerBand = data?.VerifiedSkills?.getOccupationGroupAndCareerBandByCourseId?.[0];

  return {
    occupationGroup: occupationGroupAndCareerBand?.occupationGroup,
    careerBand: occupationGroupAndCareerBand?.careerBand,
    loading,
  };
};

export default useGetOccupationGroupAndCareerBand;
